import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  Grid,
  Text,
  VStack,
} from '@chakra-ui/react';

import DateField from 'components/fields/DateField';
import {
  parseDateString,
  formatDateToString,
} from 'components/fields/auto/resolvers/resolverUtils';
import { useAnswerCodes } from '../hooks/useAnswerCodes';
import type { PropertyInsuranceHistoryData } from '../steps/policy-info/types';

export interface PropertyDiscountsModalProps {
  isOpen: boolean;
  onClose: () => void;
  discountData: PropertyInsuranceHistoryData;
  onChange: (data: Partial<PropertyInsuranceHistoryData>) => void;
  onSave: () => void;
}

const PropertyDiscountsModal = ({
  isOpen,
  onClose,
  discountData,
  onChange,
  onSave,
}: PropertyDiscountsModalProps) => {
  const { getOptions, isLoading } = useAnswerCodes();

  const membershipGroupOptions = getOptions(
    'hab_membership_discount_membership_group',
  );
  const additionalMembershipGroupOptions = getOptions(
    'hab_additional_membership_discount_membership_group',
  );

  const hasPrimaryGroup = Boolean(
    discountData.membership_discount_membership_group,
  );
  const hasAdditionalGroup = Boolean(
    discountData.additional_membership_discount_membership_group,
  );

  const isPrimaryNumberRequired = hasPrimaryGroup;
  const isPrimaryDateRequired = hasPrimaryGroup;
  const isAdditionalNumberRequired = hasAdditionalGroup;
  const isAdditionalDateRequired = hasAdditionalGroup;

  const additionalGroupOptions =
    additionalMembershipGroupOptions.length > 1
      ? additionalMembershipGroupOptions
      : membershipGroupOptions;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Membership Discounts</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <Text fontSize="sm" fontWeight="600" color="gray.600">
              Membership Discount
            </Text>
            <Grid templateColumns="1fr 1fr 140px" gap={3} alignItems="end">
              <FormControl>
                <FormLabel fontSize="sm">Group</FormLabel>
                <Select
                  variant="main"
                  size="sm"
                  value={
                    discountData.membership_discount_membership_group || ''
                  }
                  onChange={(e) => {
                    const newGroup = e.target.value;
                    if (newGroup) {
                      onChange({
                        membership_discount_membership_group: newGroup,
                      });
                    } else {
                      onChange({
                        membership_discount_membership_group: '',
                        membership_discount_membership_number: '',
                        membership_discount_membership_effective_date:
                          undefined,
                        additional_membership_discount_membership_group: '',
                        additional_membership_discount_membership_number: '',
                        additional_membership_discount_membership_effective_date:
                          undefined,
                      });
                    }
                  }}
                  isDisabled={isLoading}
                >
                  {membershipGroupOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm">
                  Number{isPrimaryNumberRequired ? ' *' : ''}
                </FormLabel>
                <Input
                  variant="main"
                  size="sm"
                  value={
                    discountData.membership_discount_membership_number || ''
                  }
                  onChange={(e) =>
                    onChange({
                      membership_discount_membership_number: e.target.value,
                    })
                  }
                  placeholder="Membership #"
                  isInvalid={
                    isPrimaryNumberRequired &&
                    !discountData.membership_discount_membership_number
                  }
                />
              </FormControl>
              <DateField
                label={`Date${isPrimaryDateRequired ? ' *' : ''}`}
                date={parseDateString(
                  discountData.membership_discount_membership_effective_date ||
                    '',
                )}
                onChange={(date: Date | null) =>
                  onChange({
                    membership_discount_membership_effective_date:
                      formatDateToString(date) || undefined,
                  })
                }
                size="sm"
                isInvalid={
                  isPrimaryDateRequired &&
                  !discountData.membership_discount_membership_effective_date
                }
              />
            </Grid>

            <Text fontSize="sm" fontWeight="600" color="gray.600" mt={2}>
              Additional Membership Discount
            </Text>
            <Grid templateColumns="1fr 1fr 140px" gap={3} alignItems="end">
              <FormControl>
                <FormLabel fontSize="sm">Group</FormLabel>
                <Select
                  variant="main"
                  size="sm"
                  value={
                    discountData.additional_membership_discount_membership_group ||
                    ''
                  }
                  onChange={(e) => {
                    const newGroup = e.target.value;
                    if (newGroup) {
                      onChange({
                        additional_membership_discount_membership_group:
                          newGroup,
                      });
                    } else {
                      onChange({
                        additional_membership_discount_membership_group: '',
                        additional_membership_discount_membership_number: '',
                        additional_membership_discount_membership_effective_date:
                          undefined,
                      });
                    }
                  }}
                  isDisabled={isLoading || !hasPrimaryGroup}
                >
                  {additionalGroupOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm">
                  Number{isAdditionalNumberRequired ? ' *' : ''}
                </FormLabel>
                <Input
                  variant="main"
                  size="sm"
                  value={
                    discountData.additional_membership_discount_membership_number ||
                    ''
                  }
                  onChange={(e) =>
                    onChange({
                      additional_membership_discount_membership_number:
                        e.target.value,
                    })
                  }
                  isDisabled={!hasPrimaryGroup}
                  placeholder="Membership #"
                  isInvalid={
                    isAdditionalNumberRequired &&
                    !discountData.additional_membership_discount_membership_number
                  }
                />
              </FormControl>
              <DateField
                label={`Date${isAdditionalDateRequired ? ' *' : ''}`}
                date={parseDateString(
                  discountData.additional_membership_discount_membership_effective_date ||
                    '',
                )}
                onChange={(date: Date | null) =>
                  onChange({
                    additional_membership_discount_membership_effective_date:
                      formatDateToString(date) || undefined,
                  })
                }
                size="sm"
                isDisabled={!hasPrimaryGroup}
                isInvalid={
                  isAdditionalDateRequired &&
                  !discountData.additional_membership_discount_membership_effective_date
                }
              />
            </Grid>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Cancel
          </Button>
          <Button colorScheme="yellow" onClick={onSave}>
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default PropertyDiscountsModal;
